import { Inject, Injectable } from '@angular/core';
import { concat, Observable, ReplaySubject } from 'rxjs';
import { CONFIG_TOKEN, WordpressConfiguration, WordpressModule } from './wordpress.module';
import { WpGlobalLabels } from './types/wp';
import { HttpClient } from '@angular/common/http';
import { WpPageList } from '@streamamg/wordpress-lib/src/lib/types/wp';

@Injectable({
  providedIn: WordpressModule,
})
export class WordpressService {
  private cacheDataByUrl: { [url: string]: ReplaySubject<any> };

  private readonly globalSettingsUrl: string = '?postType=global_settings';
  private pagesUrl: string[];
  private wpPages: WpPageList = {};
  private wpFixedPages: WpPageList = {};
  private wordpressPath: string;
  private globalSettingsMapping: {
    labels?: string,
  } = {
    labels: 'labels',
  };

  private cachedWpGlobalSettings: { [key: string]: any };

  constructor(
    @Inject(CONFIG_TOKEN) private config: WordpressConfiguration,
    private httpClient: HttpClient) {
    this.cacheDataByUrl = {};
    this.cachedWpGlobalSettings = {};

    this.wordpressPath = config.wordpressPath;
    this.globalSettingsUrl = config.globalSettingsUrl || this.globalSettingsUrl;
    this.pagesUrl = config.pagesUrl;
    this.globalSettingsMapping = config.globalSettingsMapping || this.globalSettingsMapping;

  }

  public setCachedData<WpGlobalSettings>(): Observable<boolean> {

    let isReady = new ReplaySubject<boolean>(1);

    if (!Object.keys(this.cachedWpGlobalSettings).length) {

      this.getData<WpGlobalSettings>(this.globalSettingsUrl).subscribe(
        {
          next: (value: WpGlobalSettings) => {
            this.cachedWpGlobalSettings = value;

            isReady.next(true);
          },
          error: (err) => {
            isReady.error(err);
          },
        },
      );
    } else {

      isReady.next(true);
    }

    return isReady.asObservable();
  }

  public getLabels(): WpGlobalLabels {

    return this.cachedWpGlobalSettings[this.globalSettingsMapping.labels];
  }

  public getPagesPaths<T>(): T {

    return this.wpFixedPages as unknown as T;
  }

  public getPageList(): WpPageList {

    return this.wpPages;
  }

  public getWpGlobalSettings<WpGlobalSettings>(): WpGlobalSettings {

    return this.cachedWpGlobalSettings as WpGlobalSettings;
  }

  protected generatePageUrl(path: string): string {
    return `${this.wordpressPath}${path}`;
  }

  /**
   * Grabs the data from the memory cache if available, else it will trigger a call and retrieves the data.
   * @param pagePath WP page path
   */
  public getPageData<D>(pagePath: string): Observable<D> {
    return this.getData<D>(this.generatePageUrl(pagePath));
  }

  public getData<T>(url: string): Observable<T> {

    if (!this.cacheDataByUrl[url]) {
      this.cacheDataByUrl[url] = new ReplaySubject<T>(1);

      this.httpClient.get(url).subscribe(
        {
          next: (value) => {
            this.cacheDataByUrl[url].next(value);
          },
          error: (err) => {
            console.error('WP Ajax call has gone wrong :-( ');
            this.cacheDataByUrl[url].error(err);
          },
        },
      );
    }

    return this.cacheDataByUrl[url].asObservable() as Observable<T>;
  }

  public setPages(): Observable<boolean> {

    const subject = new ReplaySubject<boolean>(1);

    concat(...this.pagesUrl.map(
      (url) => {
        const wpUrl = new URL(url);
        wpUrl.searchParams.set('wpConfsOnly', '1');

        return this.httpClient.get(wpUrl.href);
      }),
    ).subscribe({
      next: (wpPages: WpPageList) => {

        this.wpPages = {...this.wpPages, ...wpPages};

        Object.values(wpPages).filter((wpPage) => wpPage.confs.page_key).forEach((wpPage) => {

          this.wpFixedPages[wpPage.confs.page_key] = wpPage;
        });
      },
      error: (err) => {
        console.error('WP Ajax call has gone wrong :-( ');
        subject.error(err);
      },
      complete: () => {
        subject.next(true);
      },
    });

    return subject.asObservable();
  }
}
